'use client';

import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useQueryConfig } from '@/hooks/queries/useQueryConfig';
import { useSpaceLanguagesQuery } from '@/hooks/queries/useUserAddonsQuery';

import { useEditingLanguageStore } from './provider';
import type { UseEditingLanguageResult } from './types';

// ------------- Primary Hook -------------

/**
 * Hook to access and manage the editing language.
 *
 * - Syncs editable languages from spaceLanguages endpoint (prefetched for all roles).
 *   Returns configured editableLanguages when set, otherwise all supportedLanguages.
 * - Editing language is a global, per-user preference persisted via zustand persist cookie
 * - All consumers share the same reactive Zustand state
 */
export function useEditingLanguage(): UseEditingLanguageResult {
  const { httpRequestConfig } = useQueryConfig();

  const {
    editingLanguage,
    editableLanguages,
    setEditingLanguage,
    setEditableLanguages,
  } = useEditingLanguageStore(
    useShallow(state => ({
      editingLanguage: state.editingLanguage,
      editableLanguages: state.editableLanguages,
      setEditingLanguage: state.setEditingLanguage,
      setEditableLanguages: state.setEditableLanguages,
    })),
  );

  const { data: spaceLanguagesResponse } = useSpaceLanguagesQuery({ httpRequestConfig });

  const apiEditableLanguages
    = spaceLanguagesResponse?.data?.editableLanguages
      ?? spaceLanguagesResponse?.data?.supportedLanguages
      ?? [];

  // Sync editable languages from API into the store
  useEffect(() => {
    if (apiEditableLanguages.length > 0) {
      setEditableLanguages(apiEditableLanguages);
    }
  }, [apiEditableLanguages, setEditableLanguages]);

  // Use API data directly if store hasn't been hydrated yet (avoids flash on refresh)
  const resolvedEditableLanguages = editableLanguages.length > 0
    ? editableLanguages
    : apiEditableLanguages;

  return {
    editingLanguage,
    setEditingLanguage,
    editableLanguages: resolvedEditableLanguages,
  };
}

// ------------- Individual Selectors -------------

/**
 * Get current editing language only.
 * Use when you only need the editing language value.
 */
export function useCurrentEditingLanguage(): string {
  return useEditingLanguageStore(state => state.editingLanguage);
}

/**
 * Get editable languages list only.
 */
export function useEditableLanguages(): string[] {
  return useEditingLanguageStore(state => state.editableLanguages);
}
